import React, {useEffect, useRef, useState} from "react";
import {Button, Table, Typography} from 'antd';
import dayjs from "dayjs";

import {getActiveAccount} from "../../lib/activeMember";
import {messageType, showMessages} from "../../helpers/message";
import {getLegacyInvoicePDF, getLegacyInvoices} from "../../helpers/invoice";
import EngageCurrency from "../billings/EngageCurrency";
import {dateFormat} from "../../lib/time";

const LegacyInvoices = () => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    //region fetch Invoices
    const [legacyInvoices, setLegacyInvoices] = useState([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(10);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchLegacyInvoices = async (page, perPage, abortSignal) => {
            setLoading(true);

            const account = getActiveAccount();
            if (account === null) {
                showMessages('No Account attached!', messageType.error);
                setLoading(false);
                return;
            }
            const accountID = account.id;

            try {
                const response = await getLegacyInvoices({accountID, page, perPage}, abortSignal);

                if (abortSignal.aborted === false) {
                    setLegacyInvoices(response.data);
                    setPage(response.current_page);
                    setPerPage(response.per_page);
                    setTotal(response.total);
                    setLoading(false);
                }

            } catch (e) {
                showMessages(e.message, messageType.error);
                setLoading(false);
                if(e.status_code === 404) window.location.replace('/404');
            }
        }

        const abortSignal = abortSignalRef.current;
        fetchLegacyInvoices(page, perPage, abortSignal);
    }, [page, perPage])

    //endregion

    //region download PDF
    const [href, setHref] = useState('');

    const aRef = useRef();

    const getFileName = (invoiceStartDate) => {
        const endDate = dayjs(invoiceStartDate).tz().endOf('month');
        return 'Invoice' + dayjs(invoiceStartDate).tz().format('MMMD') + '-' +
            dayjs(endDate).tz().format('MMMD') + '.pdf';
    }

    const onClick = async (invoice) => {
        setLoading(true);
        const abortSignal = abortSignalRef.current;

        try {
            const blob = await getLegacyInvoicePDF(invoice.invoice_number, abortSignal);

            if (abortSignal.aborted === false) {
                const blobUrl = URL.createObjectURL(blob);
                setHref(blobUrl);

                // Set PDF filename based on invoice time range
                aRef.current.download = getFileName(invoice.start_date);

                // Issue with React 18 downloading files, confirmed bug in their repo.
                // Wrap with a timeout.
                setTimeout(() => {
                    aRef.current.click();
                    URL.revokeObjectURL(blobUrl);
                }, 100);

                setLoading(false);
            }

        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
        }
    }
    //endregion

    //region Table
    const columns = [
        {
            title: <Typography.Text strong>Date Range</Typography.Text>,
            key: 'date',
            render: (invoice) => {
                const endDate = dayjs(invoice.start_date).tz().endOf('month');
                return dayjs(invoice.start_date).tz().format(dateFormat) + ' - ' + endDate.format(dateFormat)
            }

        },
        {
            title: <Typography.Text strong>Invoice Number</Typography.Text>,
            key: 'number',
            render: invoice => '#' + invoice.invoice_number,
        },
        {
            title: <Typography.Text strong>Balance</Typography.Text>,
            key: 'balance',
            render: invoice => <EngageCurrency
                amount={parseFloat(invoice.balance)}
            />
        },
        {
            title: <Typography.Text strong>Due Date</Typography.Text>,
            key: 'due_date',
            render: invoice => {
                let dueDate = 'N/A';
                if (invoice.due_date) {
                    dueDate = dayjs(invoice.due_date).tz().format(dateFormat);
                }
                return <span className="due-date-line-item">{dueDate}</span>
            },
        },
        {
            key: 'download',
            render: invoice => {
                return <>
                    <Button onClick={() => onClick(invoice)} type="primary" ghost>
                        View PDF
                    </Button>
                    <a
                        ref={aRef}
                        href={href}
                        className="hidden-anchor"
                    >
                        download invoice
                    </a>
                </>
            }
        },
    ];

    const onChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
    };

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total,
        onChange,
        showSizeChanger: true,
        onShowSizeChange: onChange,
    };
    //endregion

    // Cleanup effect when component unmounts
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    return (
        <Table
            rowKey="invoice_number"
            dataSource={legacyInvoices}
            columns={columns}
            bordered={false}
            className="responsive-table expandable-table account-invoices"
            pagination={paginationAttr}
            loading={loading}
        />
    )
};

export default LegacyInvoices;
